import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { Briefcase, BudgetPig, ClientTie, Clock, Ellipsis } from 'components/Icons';
import NotificationCategoryIcon from 'components/notification-feed/NotificationCategoryIcon';
import ActivityMessage from 'components/ui-blocks/activities-table/ActivityMessage';
import { Td, Tr } from 'components/ui-blocks/table';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import { Link } from 'react-router-dom';
import { AnomalyActivityTypes, RegularActivityTypes } from 'redux/modules/activities.types';
import { NotificationColors } from 'redux/modules/notifications.types';
import type { Activity } from 'types/activity';
import styles from './ActivitiesTable.module.scss';

interface Props {
  activity: Activity;
  showContextColumn?: boolean | null | undefined;
}

function getCategoryIcon(category: RegularActivityTypes | AnomalyActivityTypes) {
  switch (category) {
    case RegularActivityTypes.project_budget_progress:
    case RegularActivityTypes.project_budget_progress_eighty:
    case RegularActivityTypes.project_budget_progress_fifty:
    case RegularActivityTypes.project_budget_progress_hundred:
      return <BudgetPig />;
    case RegularActivityTypes.invite_project:
    case RegularActivityTypes.invite_multiple_project:
    case RegularActivityTypes.create_project:
    case RegularActivityTypes.delete_project:
    case RegularActivityTypes.archive_project:
      return <Briefcase />;
    case RegularActivityTypes.archive_client:
    case RegularActivityTypes.create_client:
      return <ClientTie />;
    case RegularActivityTypes.logged_first_time:
    case AnomalyActivityTypes.user_logged_odd_duration:
    case AnomalyActivityTypes.user_logged_odd_time_from:
    case AnomalyActivityTypes.user_logged_odd_time_to:
    case AnomalyActivityTypes.user_logged_rare_project:
    case AnomalyActivityTypes.user_logged_on_odd_day:
    case AnomalyActivityTypes.user_logged_project_at_odd_interval:
    case AnomalyActivityTypes.user_logged_with_odd_logging_lag:
    case AnomalyActivityTypes.user_logged_by_other_user:
      return <Clock />;
    default:
      return <Ellipsis />;
  }
}

export function getActionColor(type: RegularActivityTypes | AnomalyActivityTypes) {
  switch (type) {
    case RegularActivityTypes.project_budget_progress:
    case RegularActivityTypes.project_budget_progress_eighty:
    case RegularActivityTypes.project_budget_progress_fifty:
    case RegularActivityTypes.project_budget_progress_hundred:
    case RegularActivityTypes.delete_project:
      return {
        color: NotificationColors.warning,
        categoryIcon: getCategoryIcon(type),
      };
    case RegularActivityTypes.create_client:
    case RegularActivityTypes.create_project:
    case RegularActivityTypes.invite_project:
    case RegularActivityTypes.invite_multiple_project:
    case RegularActivityTypes.logged_first_time:
      return {
        color: NotificationColors.action,
        categoryIcon: getCategoryIcon(type),
      };
    case AnomalyActivityTypes.user_logged_odd_duration:
    case AnomalyActivityTypes.user_logged_odd_time_from:
    case AnomalyActivityTypes.user_logged_odd_time_to:
    case AnomalyActivityTypes.user_logged_rare_project:
    case AnomalyActivityTypes.user_logged_on_odd_day:
    case AnomalyActivityTypes.user_logged_project_at_odd_interval:
    case AnomalyActivityTypes.user_logged_with_odd_logging_lag:
    case AnomalyActivityTypes.user_logged_by_other_user:
      return {
        color: NotificationColors.anomaly,
        categoryIcon: getCategoryIcon(type),
      };
    case RegularActivityTypes.archive_project:
    case RegularActivityTypes.archive_client:
      return {
        color: NotificationColors.archived,
        categoryIcon: getCategoryIcon(type),
      };
    default:
      return { color: NotificationColors.default, icon: null };
  }
}

const ActivitiesTableRow: React.FC<Props> = props => {
  const { activity, showContextColumn } = props;
  const { project, client } = activity;
  const showProjectContext = project.id !== null && showContextColumn;
  const showClientContext = client.id !== null && showContextColumn;
  const icon = getActionColor(activity.type);

  return (
    <Tr data-testid="last_activities_list" className={styles.row}>
      {showClientContext && (
        <>
          <Td className={styles.badge}>
            <NotificationCategoryIcon color={icon.color} icon={icon.categoryIcon} size="medium" />
          </Td>
          <Td className={styles.name}>
            <DiagnosticResourceId resource="client" id={client.id!} />
            <strong className="fs-mask">{client.name}</strong>
          </Td>
        </>
      )}
      {showProjectContext && (
        <>
          <Td className={styles.badge}>
            <NotificationCategoryIcon color={icon.color} icon={icon.categoryIcon} size="medium" />
          </Td>
          <Td className={styles.name}>
            <strong>
              <DiagnosticResourceId resource="project" id={project.id!} />
              {project.isDeleted ? (
                <span className="fs-mask">
                  {project.name} <FontAwesome name="trash" />
                </span>
              ) : (
                <Link to={`/projects/${project.id}`} className="fs-mask">
                  {project.name}
                </Link>
              )}
            </strong>
          </Td>
        </>
      )}
      <Td className={styles.message}>
        <ActivityMessage activity={activity} useContextName={!showContextColumn} />
      </Td>
      <Td className={styles.date} rightAligned>
        {activity.relativeTime}
      </Td>
    </Tr>
  );
};

export default ActivitiesTableRow;
